import React, { useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  DELETE_EXTERNAL_INVITATION_CACHE_KAY,
  INVITATION_CARD_COLUMN_SPANS,
} from "features/configuration/InviteExternalUsers/constants.ts";
import FlatList from "common/Lists/FlatList.tsx";
import {
  ExternalAccountInvitation,
  ExternalAccountInvitationWithKey,
  LanguageNameSpaces,
  ExternalInvitationType,
} from "@shared/types";
import InvitationCard from "features/configuration/InviteExternalUsers/InvitationCard.tsx";
import useExpandedCardsSorters from "common/CardSorters/useExpandedCardsSorters.tsx";
import { ColumnType } from "@shared/common/CardsList/types.ts";
import { useWindowSize } from "react-use";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import EmptyView from "common/Empty/EmptyView";
import { EmptyStatesType } from "common/Icons/types.ts";

const InvitationCards = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  const { data, isLoading, isFetching } =
    sharedLib.sharedApi.configuration.useGetAccountInvitesQuery();
  const deleteAccountInviteStatus =
    sharedLib.sharedApi.configuration.useDeleteAccountInviteMutation({
      fixedCacheKey: DELETE_EXTERNAL_INVITATION_CACHE_KAY,
    })[1];
  const { height } = useWindowSize();

  const parsedData = useMemo(
    () =>
      data
        ?.filter(
          (invitation) => invitation.type !== ExternalInvitationType.PERSONAL
        )
        .map((invitation) => ({ ...invitation, key: invitation.token })) || [],
    [data]
  );

  const columns: ColumnType<ExternalAccountInvitation>[] = [
    {
      key: InvitationsColumn.ACCOUNT_NAME,
      span: INVITATION_CARD_COLUMN_SPANS[0],
      title: t("invitations.client_name"),
    },
    {
      key: InvitationsColumn.STATUS,
      span: INVITATION_CARD_COLUMN_SPANS[1],
      title: t("invitations.status"),
    },
  ];

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useExpandedCardsSorters({
      data: parsedData,
      columns,
    });

  const renderContent = ({
    item,
  }: {
    item: ExternalAccountInvitationWithKey;
  }) => {
    return (
      <InvitationCard
        invitation={item}
        isExpanded={isExpanded(item?.key)}
        toggleExpanded={toggleExpanded}
      />
    );
  };

  return (
    <div style={{ height: height * 0.6 }}>
      <FlatList
        data={sortedData || []}
        loading={isLoading}
        renderContent={renderContent}
        renderTopContainer={() => (
          <div style={{ marginBottom: 12 }}>{cardsSorters}</div>
        )}
        gap={12}
        refreshing={isFetching || deleteAccountInviteStatus.isLoading}
        hasActionButton={true}
        emptyState={
          isNewLook ? (
            <EmptyView
              iconKey={EmptyStatesType.Users}
              iconWidth={100}
              title={tCommon("empty.no_data")}
            />
          ) : undefined
        }
      />
    </div>
  );
};

export default InvitationCards;

enum InvitationsColumn {
  ACCOUNT_NAME = "accountName",
  STATUS = "status",
}
